import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Chip
} from '@mui/material'
import SignaturePad from 'signature_pad'
import { useProtocolSign, useProtocolsList, useProcotol } from '@services/protocols/protocols.hook'
import { usePersonDetails } from '@services/contacts/contacts.hooks'
import { useParams } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import ProtocolControlQuestionsSummary from '@components/ProtocolControlQuestionsSummary'
import WISConsentClauses from '@components/WISConsentClauses'
import BankAccountConfirmation from '@components/BankAccountConfirmation'

function toDataURL(src: any, callback: any) {
  const image = new Image()
  image.crossOrigin = 'Anonymous'
  image.onload = function () {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.height = (this as any).naturalHeight
    canvas.width = (this as any).naturalWidth
    ;(context as any).drawImage(this, 0, 0)
    const dataURL = canvas.toDataURL('image/png')
    callback(dataURL)
    canvas.remove()
  }
  image.src = src
}

// Backend stores the communication language either as an id (1=pl, 2=en, 3=ru, 4=ua)
// or as a language code. Clauses default to EN whenever the known language is not Polish.
const getClauseLanguage = (personLanguage: string | number | undefined): 'pl' | 'en' => {
  if (personLanguage === undefined || personLanguage === null || personLanguage === '') {
    return 'pl'
  }
  const isPolish =
    typeof personLanguage === 'number' ? personLanguage === 1 : personLanguage === 'pl'
  return isPolish ? 'pl' : 'en'
}

function ProtocolSign({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { refetchProtocols } = useProtocolsList()
  const { signProtocol, locators } = useProtocolSign(Number(id))
  const [person, setPerson] = useState<any>(null)
  const [numberOfLocators, setNumberOfLocators] = useState<number>(0)
  const { protocol, refreshProtocol, isLoading } = useProcotol(Number(id))
  const [isUnilateral, setIsUnilateral] = useState<boolean>(!protocol?.protocolWithTenant || false)
  const [unilateralReason, setUnilateralReason] = useState<string>(
    protocol?.oneSidedProtocolReason || ''
  )
  const [unilateralReasonError, setUnilateralReasonError] = useState(false)
  const [clientSelectionError, setClientSelectionError] = useState(false)
  const [clientRepresentativeNameError, setClientRepresentativeNameError] = useState(false)
  const [clientRepresentativeSurnameError, setClientRepresentativeSurnameError] = useState(false)
  const [
    clientRepresentativeAuthorizationDocumentError,
    setClientRepresentativeAuthorizationDocumentError
  ] = useState(false)
  const [clientHasRepresentative, setClientHasRepresentative] = useState<boolean>(
    protocol?.clientHasRepresentative || false
  )
  const [clientRepresentativeName, setClientRepresentativeName] = useState<string>(
    protocol?.clientRepresentativeName || ''
  )
  const [clientRepresentativeSurname, setClientRepresentativeSurname] = useState<string>(
    protocol?.clientRepresentativeSurname || ''
  )
  const [clientRepresentativeAuthorizationDocument, setClientRepresentativeAuthorizationDocument] =
    useState<File | undefined>()
  const [r4rSignaturePad, setR4rSignaturePad] = useState<SignaturePad>()
  const [clientSignaturePad, setClientSignaturePad] = useState<SignaturePad>()
  // WIS consent clauses state
  const [tenantMaintenance, setTenantMaintenance] = useState<boolean>(
    protocol?.tenantMaintenance === true
  )
  const [gardenMaintenanceGroundFloor, setGardenMaintenanceGroundFloor] = useState<boolean>(
    protocol?.gardenMaintenanceGroundFloor === true
  )
  const [postHandoverDefects24h, setPostHandoverDefects24h] = useState<boolean>(
    protocol?.postHandoverDefects24H === true
  )
  const [tenantImprovements, setTenantImprovements] = useState<boolean>(
    protocol?.tenantImprovements === true
  )
  const [masterKeyConsent, setMasterKeyConsent] = useState<boolean>(
    protocol?.masterKeyConsent === true
  )
  const [wisClausesError, setWisClausesError] = useState(false)
  // Client details are fetched only for WIS contracts to detect the client's language
  const { data: personDetails } = usePersonDetails(
    protocol?.isWisContract && person?.id ? person.id : null
  )
  const getPad = async () => {
    const canvas = document.querySelector('#pad')
    if (canvas) {
      ;(canvas as any).getContext('2d').scale(1, 1)
      const clientSignaturePad = new SignaturePad(canvas as HTMLCanvasElement, {
        backgroundColor: 'rgb(255, 255, 255)'
      })
      if (protocol?.clientSignature !== null) {
        toDataURL(protocol?.clientSignature, function (dataUrl: any) {
          clientSignaturePad.fromDataURL(dataUrl, {
            ratio: 1
          })
        })
      }

      setClientSignaturePad(clientSignaturePad)
    }

    const canvasR4r = document.querySelector('#padR4R')
    if (canvasR4r) {
      ;(canvasR4r as any).getContext('2d').scale(1, 1)
      const r4rSignaturePad = new SignaturePad(canvasR4r as HTMLCanvasElement, {
        backgroundColor: 'rgb(255, 255, 255)'
      })

      if (protocol?.r4rRepSignature !== null) {
        toDataURL(protocol?.r4rRepSignature, function (dataUrl: any) {
          r4rSignaturePad.fromDataURL(dataUrl, {
            ratio: 1
          })
        })
      }
      setR4rSignaturePad(r4rSignaturePad)
    }
  }

  useEffect(() => {
    getPad()
    // Update WIS consent states when protocol changes
    if (protocol) {
      setTenantMaintenance(protocol.tenantMaintenance === true)
      setGardenMaintenanceGroundFloor(protocol.gardenMaintenanceGroundFloor === true)
      setPostHandoverDefects24h(protocol.postHandoverDefects24H === true)
      setTenantImprovements(protocol.tenantImprovements === true)
      setMasterKeyConsent(protocol.masterKeyConsent === true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol, isUnilateral])

  useEffect(() => {
    // Auto-select person only if it's already set in protocol
    if (person === null && locators.length > 0 && protocol?.person !== null) {
      const personFromProtocol = locators.find((locator) => locator.id === protocol?.person)
      setPerson(personFromProtocol)
    }
    // Don't auto-select even if there's only one locator - let user choose

    if (locators.length) {
      setNumberOfLocators(locators.length)
    }
  }, [locators, person, protocol?.person])

  const validateForm = () => {
    let isValid = true

    // Validate unilateral reason when protocol is unilateral
    if (isUnilateral && !unilateralReason) {
      setUnilateralReasonError(true)
      isValid = false
    } else {
      setUnilateralReasonError(false)
    }

    // Validate client selection when protocol is not unilateral
    // Client is mandatory ONLY if there's no client representative
    if (!isUnilateral && !person && !clientHasRepresentative) {
      setClientSelectionError(true)
      isValid = false
    } else {
      setClientSelectionError(false)
    }

    // Validate WIS consent clauses if it's a WIS contract and not unilateral
    if (protocol?.isWisContract && !isUnilateral) {
      if (
        !tenantMaintenance ||
        !gardenMaintenanceGroundFloor ||
        !postHandoverDefects24h ||
        !tenantImprovements ||
        !masterKeyConsent
      ) {
        setWisClausesError(true)
        isValid = false
      } else {
        setWisClausesError(false)
      }
    }

    if (clientHasRepresentative) {
      if (!clientRepresentativeName) {
        setClientRepresentativeNameError(true)
        isValid = false
      } else {
        setClientRepresentativeNameError(false)
      }

      if (!clientRepresentativeSurname) {
        setClientRepresentativeSurnameError(true)
        isValid = false
      } else {
        setClientRepresentativeSurnameError(false)
      }

      if (
        !protocol?.clientRepresentativeAuthorizationDocument &&
        !clientRepresentativeAuthorizationDocument
      ) {
        setClientRepresentativeAuthorizationDocumentError(true)
        isValid = false
      } else {
        setClientRepresentativeAuthorizationDocumentError(false)
      }
    }

    return isValid
  }

  const save = () => {
    if (!validateForm()) {
      return
    }

    const data = isUnilateral ? undefined : (clientSignaturePad as SignaturePad).toDataURL()
    const r4rSignature = (r4rSignaturePad as SignaturePad).toDataURL()
    signProtocol
      .mutateAsync({
        sign: isUnilateral ? undefined : data,
        r4rRepSignature: r4rSignature,
        person: isUnilateral ? undefined : person?.id,
        numberOfLocators,
        protocolWithTenant: !isUnilateral,
        oneSidedProtocolReason: isUnilateral ? unilateralReason : undefined,
        clientHasRepresentative,
        clientRepresentativeName,
        clientRepresentativeSurname,
        clientRepresentativeAuthorizationDocument,
        // Include WIS consent clauses
        tenantMaintenance,
        gardenMaintenanceGroundFloor,
        postHandoverDefects24H: postHandoverDefects24h,
        tenantImprovements,
        masterKeyConsent
      })
      .then(() => {
        refetchProtocols()
        refreshProtocol()
      })
  }

  useEffect(() => {
    if (unilateralReason.length) {
      setUnilateralReasonError(false)
    }
  }, [unilateralReason])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}
    >
      {protocol?.isWisContract && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Chip label='Umowa WIS' color='primary' />
        </Box>
      )}

      <ProtocolControlQuestionsSummary />

      {/* Bank Account Confirmation - only show if not unilateral and person is selected */}
      {!isUnilateral && person && (
        <BankAccountConfirmation
          personId={person?.id || null}
          isReadOnly={isReadOnly}
          language='pl'
        />
      )}

      <Box sx={{ width: '100%', marginLeft: '2rem' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isUnilateral}
              onChange={() => {
                setIsUnilateral(!isUnilateral)
              }}
            />
          }
          label='Protokół jednostronny'
        />
      </Box>
      {isUnilateral && (
        <Box sx={{ width: '100%', marginBottom: '2rem' }}>
          <TextField
            error={unilateralReasonError}
            helperText={unilateralReasonError ? 'Pole wymagane' : undefined}
            fullWidth
            required
            label='Powód, dla którego protokół jest jednostronny'
            disabled={isReadOnly}
            value={unilateralReason}
            onChange={({ target }) => setUnilateralReason(target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label='Liczba lokatorów'
            disabled={isReadOnly}
            value={numberOfLocators}
            onChange={({ target }) => setNumberOfLocators(Number(target.value))}
            sx={{ mt: 2 }}
            type='number'
          />
        </Box>
      )}
      {protocol?.isWisContract && !isUnilateral && (
        <Box sx={{ width: '100%', mb: 3 }}>
          <WISConsentClauses
            tenantMaintenance={tenantMaintenance}
            gardenMaintenanceGroundFloor={gardenMaintenanceGroundFloor}
            postHandoverDefects24h={postHandoverDefects24h}
            tenantImprovements={tenantImprovements}
            masterKeyConsent={masterKeyConsent}
            onChange={(field, value) => {
              switch (field) {
                case 'tenantMaintenance':
                  setTenantMaintenance(value)
                  break
                case 'gardenMaintenanceGroundFloor':
                  setGardenMaintenanceGroundFloor(value)
                  break
                case 'postHandoverDefects24h':
                  setPostHandoverDefects24h(value)
                  break
                case 'tenantImprovements':
                  setTenantImprovements(value)
                  break
                case 'masterKeyConsent':
                  setMasterKeyConsent(value)
                  break
              }
              setWisClausesError(false)
            }}
            disabled={isReadOnly}
            showError={wisClausesError}
            defaultLanguage={getClauseLanguage(personDetails?.language)}
          />
        </Box>
      )}
      {!isUnilateral && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Typography display='block' textAlign='left'>
            Ja, niżej podpisana/podpisany{' '}
            {person
              ? `${person.firstName} ${person.lastName}, `
              : clientHasRepresentative
                ? `${clientRepresentativeName} ${clientRepresentativeSurname} (przedstawiciel klienta), `
                : `[...] `}
            niniejszym oświadczam, że przyjmuję do wiadomości, że podpisanie niniejszego protokołu
            lub zwrot Przedmiotu Umowy przed upływem okresu przewidzianego w Umowie, nie jest
            równoznaczny z rozwiązaniem Umowy.
          </Typography>
          <Typography display='block' textAlign='left'>
            I, the undersigned{' '}
            {person
              ? `${person.firstName} ${person.lastName}`
              : clientHasRepresentative
                ? `${clientRepresentativeName} ${clientRepresentativeSurname} (client representative)`
                : `[...]`}{' '}
            hereby declare that I acknowledge that signing this protocol or returning the Subject of
            the Agreement before the expiry of the period provided for in the Agreement is not
            equivalent to termination of the Agreement.
          </Typography>
        </Box>
      )}

      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column-reverse',
          justifyContent: 'space-around',
          marginTop: '1rem',
          maxWidth: '50%',
          [theme.breakpoints.up('lg')]: {
            flexDirection: 'row'
          }
        })}
      >
        {!isUnilateral && (
          <Box
            sx={(theme) => ({
              flex: 1,
              marginTop: '1rem',
              [theme.breakpoints.up('lg')]: { marginRight: '2rem', marginTop: 0 }
            })}
          >
            <canvas
              id='pad'
              style={{
                border: '1px solid #000',
                backgroundColor: 'white'
              }}
            />
            <Typography display='block' textAlign='center' variant='caption'>
              Podpis klienta
            </Typography>
            <Autocomplete
              sx={{ mt: 2 }}
              value={person || null}
              onChange={(_, value: any) => {
                setPerson(value)
                setClientSelectionError(false)
              }}
              fullWidth
              options={locators}
              disableClearable={false}
              clearText='Wyczyść'
              getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={clientHasRepresentative ? 'Klient (opcjonalnie)' : 'Klient'}
                  required={!clientHasRepresentative}
                  error={clientSelectionError}
                  helperText={
                    clientSelectionError
                      ? 'Wybór klienta lub przedstawiciela jest obowiązkowy'
                      : undefined
                  }
                  fullWidth
                />
              )}
              disabled={isReadOnly}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={clientHasRepresentative}
                  disabled={isReadOnly}
                  onChange={() => {
                    setClientHasRepresentative(!clientHasRepresentative)
                  }}
                />
              }
              label='Przedstawiciel klienta'
            />

            <Box>
              {clientHasRepresentative && (
                <>
                  <TextField
                    sx={{ mt: 2 }}
                    fullWidth
                    disabled={isReadOnly}
                    id='name'
                    error={clientRepresentativeNameError}
                    helperText={clientRepresentativeNameError ? 'Pole wymagane' : undefined}
                    value={clientRepresentativeName}
                    onChange={({ target }) => setClientRepresentativeName(target.value)}
                    label='Imię przedstawiciela'
                  />

                  <TextField
                    fullWidth
                    label='Nazwisko przedstawiciela'
                    disabled={isReadOnly}
                    id='surname'
                    error={clientRepresentativeSurnameError}
                    helperText={clientRepresentativeSurnameError ? 'Pole wymagane' : undefined}
                    value={clientRepresentativeSurname}
                    onChange={({ target }) => setClientRepresentativeSurname(target.value)}
                    sx={{ mt: 2 }}
                  />

                  <Typography textAlign='left' variant='body1' sx={{ mt: 2, mb: 0, pb: 0 }}>
                    Upoważnienie
                  </Typography>

                  <TextField
                    error={clientRepresentativeAuthorizationDocumentError}
                    helperText={
                      clientRepresentativeAuthorizationDocumentError ? 'Pole wymagane' : undefined
                    }
                    fullWidth
                    sx={{ mb: 1 }}
                    id='authorizationDocument'
                    variant='outlined'
                    onChange={({ target }: any) => {
                      setClientRepresentativeAuthorizationDocument(target.files?.[0])
                    }}
                    disabled={isReadOnly}
                    type='file'
                  />

                  {protocol?.clientRepresentativeAuthorizationDocument && (
                    <FormLabel component='legend'>
                      <a href={protocol?.clientRepresentativeAuthorizationDocument} target='_blank'>
                        Aktualne Upoważnienie
                      </a>
                    </FormLabel>
                  )}
                </>
              )}
            </Box>

            <TextField
              fullWidth
              label='Liczba lokatorów'
              disabled={isReadOnly}
              value={numberOfLocators}
              onChange={({ target }) => setNumberOfLocators(Number(target.value))}
              sx={{ mt: 2 }}
              type='number'
            />
          </Box>
        )}

        <Box sx={{ flex: 1 }}>
          <canvas
            id='padR4R'
            style={{
              border: '1px solid #000',
              backgroundColor: 'white'
            }}
          />
          <Typography display='block' textAlign='center' variant='caption'>
            Podpis reprezentanta R4R
          </Typography>
        </Box>
      </Box>

      {isReadOnly ? (
        <Box>
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <Button
              variant='contained'
              sx={{ mt: 2 }}
              to={`https://crm.r4r.pl/pl/crm/protocols/protocols/view/${id}/`}
              component={NavLink}
            >
              Pokaż protokół
            </Button>
          </Box>
          <Typography mt={2} variant='body2' fontWeight={700}>
            Protokół został już podpisany
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <Button
              variant='contained'
              sx={{ mt: 2 }}
              to={`https://crm.r4r.pl/pl/crm/protocols/protocols/view/${id}/`}
              component={NavLink}
            >
              Pokaż protokół
            </Button>
            <Button
              sx={{ mt: 2 }}
              onClick={() => {
                clientSignaturePad?.clear()
                r4rSignaturePad?.clear()
              }}
            >
              Wyczyść
            </Button>
          </Box>
          <Button
            sx={{ mt: 2 }}
            variant='contained'
            onClick={save}
            disabled={!isUnilateral && !person && !clientHasRepresentative}
          >
            Podpisz protokół
          </Button>
        </>
      )}
    </Box>
  )
}

export default ProtocolSign
